import React from 'react';

const processingRows = [
  {
    purpose: 'Create and manage your account',
    data: 'Email address, password credentials managed by Supabase, account identifier, display name, profile picture, authentication provider, and account settings.',
    basis: 'Performance of our contract with you.',
    retention: 'While your account is active, then deleted when you delete your account, subject to limited legal or backup retention.',
  },
  {
    purpose: 'Provide Prompt Studio',
    data: 'Workspaces, prompts, prompt versions and drafts, agent settings, chats, AI inputs and outputs, collaboration records, model choices, and service preferences.',
    basis: 'Performance of our contract with you.',
    retention: 'While your account or relevant workspace is active, until you delete the content or account.',
  },
  {
    purpose: 'Connect and operate AI providers',
    data: 'Prompts, chat messages, instructions, model settings, related content sent to the AI provider you select, and optional OpenAI or Google Gemini API keys.',
    basis: 'Performance of our contract with you.',
    retention: 'Content is retained in Promptera as described above. Provider-side retention is governed by the selected provider and your agreement or settings with that provider.',
  },
  {
    purpose: 'Measure usage and enforce service limits',
    data: 'Model and provider used, token counts, request identifiers, timestamps, quota and subscription information, status, and limited error information.',
    basis: 'Performance of our contract and our legitimate interest in operating and improving the service.',
    retention: 'While your account is active, then deleted with your account unless needed for a legal claim.',
  },
  {
    purpose: 'Enable workspace collaboration',
    data: 'Name, email address, profile picture, workspace role, and temporary editor-presence information shared with authorized workspace members.',
    basis: 'Performance of our contract with you.',
    retention: 'Membership records are kept while you belong to the workspace; editor-presence information is temporary.',
  },
  {
    purpose: 'Secure and operate the service',
    data: 'IP address, request and device information, authentication events, and server or error logs.',
    basis: 'Our legitimate interests in security, fraud prevention, troubleshooting, and service reliability.',
    retention: 'Kept for a limited operational period, then deleted or overwritten, unless needed to investigate an incident or legal claim.',
  },
  {
    purpose: 'Respond to enquiries',
    data: 'Name, email address, subject, message, and subsequent correspondence you send us.',
    basis: 'Taking steps at your request or our legitimate interest in responding to enquiries.',
    retention: 'Retained only as long as needed to handle the enquiry and any related legal obligations.',
  },
  {
    purpose: 'Understand use of our public website and apps',
    data: 'Page URL, page title, interaction events, approximate location, device or browser information, and session recordings collected through Google Analytics for Firebase and Microsoft Clarity.',
    basis: 'Your consent.',
    retention: 'According to the configured analytics provider retention periods.',
  },
  {
    purpose: 'Meet legal obligations and protect rights',
    data: 'Information necessary to respond to lawful requests, enforce agreements, and establish or defend legal claims.',
    basis: 'Legal obligation or our legitimate interests in protecting our rights.',
    retention: 'For the period required by law or needed for the relevant claim.',
  },
];

function Section({ title, children }) {
  return (
    <section className="privacy-section">
      <h2>{title}</h2>
      {children}
    </section>
  );
}

export default function PrivacyPage() {
  return (
    <main className="privacy-page">
      <div className="container privacy-container">
        <header className="privacy-hero">
          <h1>Privacy Notice</h1>
          <p className="privacy-effective">Effective date: 12 June 2026</p>
          <p>
            Promptera provides an online workspace for creating, testing, improving, and collaborating on prompts and AI agents.
            This notice explains how Promptera processes personal data when you visit our website, contact us,
            or use Prompt Studio.
          </p>
          <p>
            Promptera is the controller for the processing described in this notice. For privacy questions or to exercise your
            rights, contact <a href="mailto:info@promptera.io">info@promptera.io</a>.
          </p>
        </header>

        <article className="privacy-content glass">
          <Section title="What We Process and Why">
            <div className="privacy-table-wrap">
              <table className="privacy-table">
                <thead>
                  <tr>
                    <th>Purpose</th>
                    <th>Personal data</th>
                    <th>Legal basis</th>
                    <th>Retention</th>
                  </tr>
                </thead>
                <tbody>
                  {processingRows.map((row) => (
                    <tr key={row.purpose}>
                      <th scope="row">{row.purpose}</th>
                      <td>{row.data}</td>
                      <td>{row.basis}</td>
                      <td>{row.retention}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p>
              You must provide the data needed to create an account and operate requested features. Without it, we may be unable
              to provide those features. Optional profile information and personal API keys are not required.
            </p>
          </Section>

          <Section title="Data From Other Sources">
            <p>
              If you sign in or connect an account using Google or GitHub, we receive account information made available by that
              provider, such as your provider account identifier, email address, display name, profile picture, and
              connected-provider information. The information received depends on the provider and the permissions you approve.
            </p>
          </Section>

          <Section title="AI Providers">
            <p>
              When you use an AI-powered feature, Promptera sends the content needed to perform your request to the provider you
              select. The current supported providers are <strong>OpenAI</strong> and <strong>Google Gemini</strong>. This may
              include prompts, chats, agent instructions, and prompt-enhancement content.
            </p>
            <p>
              You may optionally provide your own OpenAI or Google Gemini API key. Stored API keys are encrypted in Promptera&apos;s
              database and can be removed in your settings. The key is used to authenticate requests to the relevant provider.
            </p>
            <p>
              Promptera does not use AI to make solely automated decisions about you that produce legal or similarly significant
              effects. Do not submit personal data or confidential information to an AI feature unless you are authorized to do
              so and it is necessary for your use of the service.
            </p>
          </Section>

          <Section title="Cookies and Browser Storage">
            <p>
              Prompt Studio uses browser local storage and session storage for necessary functions, including authentication
              sessions, OAuth security values, interface preferences, drafts, and navigation state.
            </p>
            <p>
              Our public website and Prompt Studio use Google Analytics for Firebase and Microsoft Clarity to measure visits and interactions. These analytics providers may use
              cookies or similar identifiers and may receive page URLs, device or browser information, IP-derived approximate
              location, and event data (including session replays and heatmaps via Clarity). Analytics requires your consent. You can also block or delete cookies using your browser
              settings.
            </p>
            <p>
              We also use Google reCAPTCHA to protect our contact forms from spam and abuse. Google reCAPTCHA sets strictly necessary security cookies and collects hardware and software information (such as device or browser data) to perform these security checks.
            </p>
          </Section>

          <Section title="Who Receives Personal Data">
            <p>We do not sell your personal data.</p>
            <p>
              We disclose personal data only where needed to provide and operate the service, where you direct us to, or where
              required by law. Recipients include:
            </p>
            <ul>
              <li><strong>OVHcloud</strong>, for hosting Prompt Studio and its database.</li>
              <li><strong>Supabase</strong>, for account authentication, password recovery, and authentication emails.</li>
              <li><strong>Google</strong>, for Google sign-in, Google Analytics for Firebase, Google reCAPTCHA, and Google Gemini when selected.</li>
              <li><strong>Microsoft</strong>, for Microsoft Clarity analytics and session recordings.</li>
              <li><strong>GitHub</strong>, when you choose GitHub sign-in or connect a GitHub account.</li>
              <li><strong>OpenAI</strong>, when you select OpenAI-powered features.</li>
              <li>Authorized members of workspaces in which you collaborate.</li>
              <li>Professional advisers, public authorities, courts, or other parties where disclosure is legally required or necessary to protect rights.</li>
            </ul>
            <p>
              Depending on the service, a provider may act as our processor or as an independent controller. Google, Microsoft, GitHub,
              OpenAI, and Supabase may process certain data as independent controllers when you use their accounts or services.
            </p>
          </Section>

          <Section title="International Transfers">
            <p>
              Some providers may process personal data outside the European Economic Area. Where GDPR rules on international
              transfers apply, transfers must be covered by a valid mechanism, such as an adequacy decision or the European
              Commission&apos;s Standard Contractual Clauses, together with supplementary measures where required. Contact{' '}
              <a href="mailto:info@promptera.io">info@promptera.io</a> for information about the safeguards relevant to your data.
            </p>
          </Section>

          <Section title="Security">
            <p>
              We use technical and organizational measures designed to protect personal data, including access controls,
              encryption in transit, restricted production access, backups, and encryption of stored user-supplied API keys. No
              service can guarantee absolute security.
            </p>
          </Section>

          <Section title="Your Rights">
            <p>Subject to the conditions in applicable law, you may ask us to:</p>
            <ul>
              <li>provide access to your personal data;</li>
              <li>correct inaccurate personal data;</li>
              <li>delete your personal data;</li>
              <li>restrict processing;</li>
              <li>provide portable data;</li>
              <li>object to processing based on legitimate interests; or</li>
              <li>withdraw consent at any time, without affecting processing carried out before withdrawal.</li>
            </ul>
            <p className="privacy-callout">
              You may object at any time to processing based on our legitimate interests. You may also object at any time to
              direct marketing, and we will stop using your data for that purpose.
            </p>
            <p>
              Prompt Studio also provides settings to remove API keys, delete cloud data, and delete your account. To exercise
              your rights or withdraw consent, contact <a href="mailto:info@promptera.io">info@promptera.io</a>. We may need to
              verify your identity before acting on a request.
            </p>
            <p>
              You may lodge a complaint with the <strong>Hellenic Data Protection Authority</strong>, Kifissias 1-3, 115 23
              Athens, Greece, using its{' '}
              <a href="https://www.dpa.gr/en/individuals/complaint-to-the-hellenic-dpa" target="_blank" rel="noreferrer">
                complaint page
              </a>
              . You may also contact another competent supervisory authority, particularly in the EU/EEA country where you live
              or work.
            </p>
          </Section>

          <Section title="Children">
            <p>
              Promptera is not directed to children under 16, and we do not knowingly collect their personal data. If you believe
              a child has provided personal data to us, contact <a href="mailto:info@promptera.io">info@promptera.io</a>.
            </p>
          </Section>

          <Section title="Changes">
            <p>
              We may update this notice when our service or processing changes. We will publish the updated notice with a new
              effective date and provide additional notice where required.
            </p>
          </Section>

          <Section title="Contact">
            <p>
              For privacy questions or requests, email <a href="mailto:info@promptera.io">info@promptera.io</a>.
            </p>
          </Section>
        </article>
      </div>
    </main>
  );
}
